import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Monitor, ShieldCheck, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import AuthBrandHeader from '../../components/AuthBrandHeader'
import { deviceAuthApi } from '../../services/deviceAuthApi'

export default function DeviceAuth() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const userCode = searchParams.get('code') ?? ''

  const [hostInfo, setHostInfo] = useState(null)
  const [loadingInfo, setLoadingInfo] = useState(true)
  const [infoError, setInfoError] = useState(null)
  const [approving, setApproving] = useState(false)
  const [approved, setApproved] = useState(false)
  const [approveError, setApproveError] = useState(null)

  useEffect(() => {
    if (!userCode) {
      setInfoError(t('auth.deviceAuth.codeMissing'))
      setLoadingInfo(false)
      return
    }

    deviceAuthApi
      .getDeviceInfo(userCode)
      .then((data) => {
        setHostInfo(data)
        setLoadingInfo(false)
      })
      .catch((err) => {
        const msg =
          err?.response?.status === 404
            ? t('auth.deviceAuth.codeInvalid')
            : t('auth.deviceAuth.fetchError')
        setInfoError(msg)
        setLoadingInfo(false)
      })
  }, [userCode, t])

  const handleApprove = async () => {
    setApproving(true)
    setApproveError(null)
    try {
      await deviceAuthApi.approveDevice(userCode)
      setApproved(true)
      setTimeout(() => navigate('/'), 2500)
    } catch (err) {
      const msg = err?.response?.data?.message ?? t('auth.deviceAuth.approveFailed')
      setApproveError(msg)
    } finally {
      setApproving(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-950 px-4 py-10">
      <div className="w-full max-w-md space-y-8">
        <AuthBrandHeader />

        <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl overflow-hidden">
          {/* Cabeçalho */}
          <div className="bg-gray-800/60 px-6 py-5 border-b border-gray-700/50 flex items-center gap-3">
            <div className="p-2 bg-blue-600/20 rounded-lg">
              <Monitor className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-base leading-tight">
                {t('auth.deviceAuth.headerTitle')}
              </h1>
              <p className="text-gray-400 text-xs mt-0.5">{t('auth.deviceAuth.headerSubtitle')}</p>
            </div>
          </div>

          <div className="px-6 py-6 space-y-5">
            {loadingInfo && (
              <div className="flex items-center justify-center gap-3 py-6 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">{t('auth.deviceAuth.loadingDevice')}</span>
              </div>
            )}

            {infoError && (
              <div className="flex items-start gap-3 p-4 bg-red-900/20 border border-red-800/40 rounded-xl">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{infoError}</p>
              </div>
            )}

            {!loadingInfo && !infoError && hostInfo && !approved && (
              <>
                <div className="text-center space-y-2 pb-2">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {t('auth.deviceAuth.hostSubtitle1')}{' '}
                    <span className="font-semibold text-white">Connect.IO</span>{' '}
                    {t('auth.deviceAuth.hostSubtitle2')}
                  </p>
                  <div className="inline-flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2">
                    <Monitor className="h-4 w-4 text-blue-400 flex-shrink-0" />
                    <span className="font-bold text-white text-lg">{hostInfo.hostName}</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-1">
                    {t('auth.deviceAuth.hostSubtitle3')}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {t('auth.deviceAuth.hostSubtitle4')}
                  </p>
                </div>

                {/* Código legível */}
                <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-xs mb-2 uppercase tracking-wider">
                    {t('auth.deviceAuth.codeLabel')}
                  </p>
                  <p className="font-mono font-bold text-2xl tracking-[0.25em] text-blue-300">
                    {hostInfo.userCode}
                  </p>
                  <p className="text-gray-500 text-xs mt-2">
                    {t('auth.deviceAuth.codeConfirm')}
                  </p>
                </div>

                {approveError && (
                  <div className="flex items-start gap-3 p-3 bg-red-900/20 border border-red-800/40 rounded-xl">
                    <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-300 text-xs">{approveError}</p>
                  </div>
                )}

                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-150"
                >
                  {approving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('auth.deviceAuth.approving')}
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4" />
                      {t('auth.deviceAuth.approveButton')}
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate('/')}
                  disabled={approving}
                  className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 transition-colors duration-150"
                >
                  {t('auth.deviceAuth.cancelButton')}
                </button>
              </>
            )}

            {approved && (
              <div className="flex flex-col items-center gap-4 py-6 text-center">
                <div className="p-3 bg-green-600/20 rounded-full">
                  <CheckCircle2 className="h-8 w-8 text-green-400" />
                </div>
                <div>
                  <p className="text-white font-semibold text-base">{t('auth.deviceAuth.accessGranted')}</p>
                  <p className="text-gray-400 text-sm mt-1">
                    <Trans
                      i18nKey="auth.deviceAuth.accessGrantedSubtitle"
                      values={{ hostName: hostInfo?.hostName || '' }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
